// /src/renderer/analysis/rendering/Panels/renderers/VRPanel.tsx
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import '../PanelContainer.scss';
import { PanelCommonProps, PanelComponentKeyType } from 'config/panelConfig';
import PanelOverlay from './PanelOverlay';
import {
  CT_PRESET_EDITOR_NAME,
  PANEL_ADJUSTMENT_NAMES,
} from 'config/labels';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';
import { usePanelContainer } from '../usePanelContainer';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import {
  EditNames,
  getCenterLineDisplayType,
  getSegShapeAnalysisType,
  isVRSurfaceSwitchType,
  PanelState,
} from 'config/analysisConfig';

import {
  getRequestSegmentation,
  getVolumeFromSeriesId,
  getVolumeMinMax,
  isToolInteracting,
} from './CornerstoneManager';

import {
  axesLineRenderer,
  cleanUpVtkRenderer,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseWheel,
  initializeVtkRenderer,
  setContainer,
  useBufferPool,
  VRContext,
  disposeServerFrontDataVtkContext,
  updateRenderWindow,
} from './vtkManagers';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import {
  renderers,
  scheduleRender,
} from '@features/analysis/rendering/Panels/renderState';
import useResizeObserver from '@features/view/useResizeObserver';
import { useConfig } from 'config/useConfig';
import type { Surface3DOverlayLabel } from 'config/config';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import { useCTPresetModal } from '@features/analysis/analysisModal/CTPresetModal/CTPresetModal';
import { useTool } from '@hooks/Tool/useTool';
import { initCameraForAnalysis, runDilateDataVolume, updateAmbientLight, updateCutter, updateVolumeColor} from '@features/view/VRUtilities';
import { centerLineRender, CPRNodeAllRenderer, handleVisibility, taviNodeAllRenderer } from '@features/view/Shared3DUtilities';
import { useBranchInfo } from '@contexts/BranchContext';
import { useLabelNamingText } from '@features/view/useLabelNamingText';
import SvgTextRenderer from '@features/view/SvgTextRender';
import SvgMeasurementText from '@features/view/SvgMeasurementText';
import { useSegmentationVolume } from '@hooks/useAnalysisForPanel/useSegmentationVolume';
import { use3DRendererManager } from '@hooks/useAnalysisForPanel/use3DRendererManager';
import { usePanelOverlayText } from '@hooks/useAnalysisForPanel/usePanelOverlayText';
import { updateOtherViews } from '@hooks/useAnalysisForPanel/useDicomUtils';
import { nodeNames, volumeKeys, volumeNames } from 'config/render3DConfig';
import { getIndexToWorld } from '@features/view/Surface3DUtilities';
import { SavedInfo } from '@models/Parameter';
import {
  buildInitialPointSphereMarkerSpecs,
  clearInitialPointSphereMarkers,
  syncInitialPointSphereMarkers,
} from '@features/view/initialPointSphereMarkers';
import { getConfiguredCprCrossSectionImageMode } from '../cprImageModeConfig';
import { getServerFrontDataAvailability } from '../cprImageModeState';
import { isServerCprFrontDataAnalysisType } from '../frontDataCprDebugLoader';

// Series Title
// (Patient Name) - (Patient Age) - (Acquisition Date) - (Acquisition Time) - (Series Description)
// text 표기, 정보 없으면 delimiter 제외, 공백

// Series Description
// -(Acquisition Date) (Acquisition Time)
// -(Accession Number)
// -(Study Description)
// -(Series Description)
// -(Image Comments)
// -Series: (Series Number)
// text 표기, 정보 없으면 공백

// Patient Description
// -(Institution Name)
// -(Patient Name)
// -(Patient ID)
// -(Patient Birth Date) (Patient Sex) (Patient Age)

// RenderType Description
// 랜더 타입(변경가능, Axial,coronal,sagittal)

// VR Orientation Reformatting Description
// Axial, Coronal, Sagittal방향에 맞는 컬러로 이루어진 회전박스(면 클릭으로 화면전환)
// 오일러 각 X,Y,Z로부터 Yaw,Pitch,Roll값을 표시
// WL은 그대로 표시, 그러나, 클릭 시 3D VR용 컨텍스트메뉴

type VRPanelPopup = {
  [Key in keyof typeof PANEL_ADJUSTMENT_NAMES]?: boolean;
};
const initialPopup: VRPanelPopup = {
  projection: false,
  preset: false,
};

interface MaterialPreset {
  intensity: number;
  diffuse: [number, number, number];
  opacity: number;
}

const VRPanel: React.FC<PanelCommonProps> = ({
  className,
  panelConfig,
  possibleProjection,
  topLeftContent,
  topRightContent,
}: PanelCommonProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const context = useRef<VRContext>(null);
  const [open, setOpen] = useState<VRPanelPopup>(initialPopup);

  const {
    selectedSeries,
    selectedSeriesId,
    selectedStudySeries,
    selectedSeriesImageTag,
  } = useBrowserPage();
  const { panelStates } = usePanelContainer();
  const {
    // selectedSeriesUid,
    isPreview,
    currentVOI,
    currentPreset,
    updateSwitchConfigs,
    globalToolState,
    panelToolState,
    updateWindowLevelWidth,
    updateVOIInvert,
    selectedLayoutConfig,
  } = useAnalysisPage();
  const {
    analysisType,
    fullscreenPanelComponentKey,
    selectedPanelComponentKey,
    swapLayoutPanelConfig,
    updatePanelState,
    updateSelectedPanelConfig,
    updateFullscreenPanelComponentKey,
  } = usePanelContainer();
  const { handleToolActivate } = useTool();
  const openCTPreset = useCTPresetModal();


  const [isReady, setIsReady] = useState<boolean>(false);
  const [svgShowText, setSvgShowText] = useState<boolean>(false);
  const [rotation, setRotation] = useState<{
    lr: number;
    si: number;
    roll: number;
  }>({ lr: 0, si: 0, roll: 0 });
  // const engine = getRenderingEngine(mainEngineId);
  const { bufferPool, setBufferPool, contextPool, setContextPool } =
    useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const chamberViewRef = useRef({
    viewNormal: null,
    normal: null,
    centers: null,
    index: 0,
  });
  const { config, setData } = useConfig();
  const refComponentKey = panelConfig.componentKey;
  const { mainBranchIds } = useBranchInfo();
  const coronaryState = panelStates['CPR'].coronary!;
  const stateMap = coronaryState.state;
  const plaueBranchMainId = coronaryState.current[0];
  const renderingQuality = config.generalSetting.renderingQuality==='high'?'high':'medium';
  const lowQuality = renderingQuality !== 'high';
  const {windowLevel,windowWidth,invert} = currentVOI;
  const isSegmentationShape = getSegShapeAnalysisType(analysisType);
  const [renderingModeMIP, setRenderingModeMIP] = useState<string>(config.renderingModeMIP);
  const isServerFrontDataVtkMode =
    getConfiguredCprCrossSectionImageMode() === 'server' &&
    isServerCprFrontDataAnalysisType(analysisType) &&
    getServerFrontDataAvailability(pool) !== 'missing';
  const isLesionStackViewLayout =
    analysisType === 'plaque' && selectedLayoutConfig.type === 'lesionStackView';
  const vrServerGenerationRef = useRef(0);

  const isStaleServerVrGeneration = useCallback(
    (generation: number) =>
      isServerFrontDataVtkMode &&
      (vrServerGenerationRef.current !== generation ||
        context.current?.activeAnalysisType !== analysisType ||
        context.current?.activeSeriesId !== selectedSeriesId),
    [isServerFrontDataVtkMode, analysisType, selectedSeriesId],
  );

  const shouldRecreateServerVtkContext = useCallback(
    (candidate?: VRContext | null) => {
      if (!candidate) return false;
      if (!candidate.serverFrontDataVtkMode && !isServerFrontDataVtkMode) {
        return false;
      }
      return (
        candidate.serverFrontDataVtkDisposed === true ||
        candidate.serverFrontDataVtkMode !== isServerFrontDataVtkMode ||
        candidate.activeAnalysisType !== (isServerFrontDataVtkMode ? analysisType : undefined) ||
        candidate.activeSeriesId !== (isServerFrontDataVtkMode ? selectedSeriesId : undefined)
      );
    },
    [isServerFrontDataVtkMode, analysisType, selectedSeriesId],
  );

  const applyLesionStackMidNodeVisibility = useCallback(() => {
    if (analysisType !== 'plaque') return;

    const current = context.current;
    const actorIds = current?.actorIds;
    if (!current || !actorIds) return;

    let changed = false;
    const visible = !isLesionStackViewLayout;
    Object.entries(actorIds).forEach(([key, actor]) => {
      if (!key.startsWith(`${nodeNames.node}.`) || !key.endsWith('.1')) return;
      if (actor.getVisibility?.() === visible) return;
      actor.setVisibility?.(visible);
      changed = true;
    });

    if (changed) {
      scheduleRender(current.viewportId, { type: 'updateRenderWindow' });
    }
  }, [analysisType, isLesionStackViewLayout]);

  useEffect(() => {
    applyLesionStackMidNodeVisibility();
  }, [applyLesionStackMidNodeVisibility]);

  type Surface3DOverlayKey = keyof Surface3DOverlayLabel;
  const surface3DOverlayLabel = useMemo(
    () =>
      config.viewOverlayLabels?.surface3D ?? {
        lesion: true,
        centerline: true,
        label: true,
        stenosisLesion: true,
      },
    [config.viewOverlayLabels?.surface3D],
  );
  const [surface3DOverlayMenuOpen, setSurface3DOverlayMenuOpen] =
    useState(false);
  const shouldShowSurface3DOverlayMenu = useMemo(
    () => ['plaque', 'fai', 'stenosis', 'calciumScore', 'isr'].includes(analysisType),
    [analysisType],
  );

  const applySurface3DOverlayVisibility = useCallback(
    (visibility: Surface3DOverlayLabel = surface3DOverlayLabel) => {
      if (!context.current) return;
      const actorIds = context.current.actorIds ?? {};
      const setVisibleByKey = (
        predicate: (key: string) => boolean,
        visible: boolean,
      ) => {
        Object.entries(actorIds).forEach(([key, actor]) => {
          if (!predicate(key)) return;
          (actor as vtkActor).setVisibility(visible);
        });
      };

      setVisibleByKey(
        (key) => new RegExp(`^${nodeNames.node}\\.\\d+\\.`).test(key),
        visibility.lesion !== false,
      );
      setVisibleByKey(
        (key) =>
          key === nodeNames.singleCenterLine ||
          key.startsWith(`${nodeNames.centerLine}.`),
        visibility.centerline !== false,
      );
      setVisibleByKey(
        (key) => key.startsWith(`${nodeNames.stenosis}.`),
        visibility.stenosisLesion !== false,
      );
      if (analysisType === 'plaque' && isLesionStackViewLayout) {
        setVisibleByKey(
          (key) =>
            new RegExp(`^${nodeNames.node}\\.\\d+\\.1$`).test(key),
          false,
        );
      }
      updateRenderWindow(context);
    },
    [analysisType, context, isLesionStackViewLayout, surface3DOverlayLabel],
  );

  useEffect(() => {
    applySurface3DOverlayVisibility();
  }, [applySurface3DOverlayVisibility]);

  const surface3DOverlayMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const overlayItems: Array<{
      key: Surface3DOverlayKey | 'all';
      label: string;
      show: boolean;
    }> = [
      { key: 'all', label: 'All', show: true },
      { key: 'lesion', label: 'Lesion', show: analysisType !== 'calciumScore' },
      { key: 'centerline', label: 'Centerline', show: true },
      { key: 'label', label: 'Label', show: true },
      {
        key: 'stenosisLesion',
        label: 'StenosisLesion',
        show: analysisType === 'stenosis',
      },
    ];
    const visibleItems = overlayItems.filter((item) => item.show);
    const visibleKeys = visibleItems
      .map((item) => item.key)
      .filter((key): key is Surface3DOverlayKey => key !== 'all');
    const allTrue =
      visibleKeys.length > 0 &&
      visibleKeys.every((key) => surface3DOverlayLabel[key] !== false);

    const applyNextOverlayLabel = (nextLabel: Surface3DOverlayLabel) => {
      setData((prev: any) => ({
        viewOverlayLabels: {
          ...prev.viewOverlayLabels,
          surface3D: nextLabel,
        },
      }));
      applySurface3DOverlayVisibility(nextLabel);
    };

    const toggle = (key: Surface3DOverlayKey | 'all') => {
      if (key === 'all') {
        const nextValue = !allTrue;
        const nextLabel = { ...surface3DOverlayLabel };
        visibleKeys.forEach((visibleKey) => {
          nextLabel[visibleKey] = nextValue;
        });
        applyNextOverlayLabel(nextLabel);
        return;
      }

      const nextLabel = {
        ...surface3DOverlayLabel,
        [key]: !surface3DOverlayLabel[key],
      };
      applyNextOverlayLabel(nextLabel);
    };

    return visibleItems.map((item) => ({
      label: item.label,
      onClick: () => toggle(item.key),
      isChecked:
        item.key === 'all'
          ? allTrue
          : surface3DOverlayLabel[item.key] !== false,
    }));
  }, [
    analysisType,
    applySurface3DOverlayVisibility,
    setData,
    surface3DOverlayLabel,
  ]);

  const surface3DOverlayButtonContent = useMemo(() => {
    if (!shouldShowSurface3DOverlayMenu) return null;
    return (
      <div
        className={`projection-text ${panelConfig.componentKey}`}
        onClick={(event) => {
          event.stopPropagation();
          setSurface3DOverlayMenuOpen(true);
        }}
      >
        Overlays
      </div>
    );
  }, [panelConfig.componentKey, shouldShowSurface3DOverlayMenu]);

  const {
     createVolume,
     createEFVolume,
     updateSegVolume,
     updateVolumeMIPColor,
     ensureVolume,
     setSegVolumeVisibility,
     baseVolumeReady,
   } = useSegmentationVolume({
     context,
     chamberViewRef,
     lowQuality,
   });


  const {
    CArmAngles,
    resetNodeRef,
    handleFocus,
    handleMarkerMove,
    handleRotate,
    handleAxes,
    handleUpdateRenderWindow,
    handleReset,
    handleOpenCArmAnglesModal,
    measurementSvgRef,
    measurementLabelItems,
    updateMeasurementLabelPositions,
  } = use3DRendererManager({
    context,
    lowQuality,
  });

  // useEffect(() => {
  //   // 캡쳐할때 브런치 이름 잠시 안보이게 하는거
  //   setSvgShowText(!config.isCapture);
  // }, [config.isCapture]);


  const {
    topContent,
    topLeftContentForCAC,
    midLeftContentRRPercent,
   } = usePanelOverlayText({});

  useEffect(() => {
    if (!ref.current) return;

    let cntx = contextPool['VR'] as VRContext | undefined;

    if (shouldRecreateServerVtkContext(cntx)) {
      disposeServerFrontDataVtkContext(
        cntx,
        `VR switch ${cntx?.activeAnalysisType ?? 'unknown'} -> ${analysisType}`,
      );
      cntx = undefined;
    }

    if (!cntx) {
      cntx = initializeVtkRenderer('VR', 'VR') as VRContext;
      setContextPool((prev) => ({ ...prev, VR: cntx }));
    }

    if (!cntx) {
      throw new Error('VR VTK context initialization failed');
    }

    cntx.serverFrontDataVtkMode = isServerFrontDataVtkMode;
    cntx.serverFrontDataVtkDisposed = false;
    cntx.activeAnalysisType = isServerFrontDataVtkMode ? analysisType : undefined;
    cntx.activeSeriesId = isServerFrontDataVtkMode ? selectedSeriesId : undefined;
    cntx.serverFrontDataRenderBlocked = isServerFrontDataVtkMode;
    if (isServerFrontDataVtkMode) {
      vrServerGenerationRef.current += 1;
    }
    setContainer(cntx, ref);
    (context as React.MutableRefObject<VRContext>).current = cntx;

    return () => {
      if (cntx?.serverFrontDataVtkMode) {
        disposeServerFrontDataVtkContext(cntx, `VR cleanup`);
        if (context.current === cntx) {
          context.current = null;
        }
        setContextPool((prev) =>
          prev.VR === cntx ? { ...prev, VR: null } : prev,
        );
        return;
      }

      cleanUpVtkRenderer(context, cntx);
      setContextPool((prev) => ({ ...prev, VR: cntx }));
    };
  }, [
    ref,
    analysisType,
    selectedSeriesId,
    isServerFrontDataVtkMode,
    shouldRecreateServerVtkContext,
  ]);


  const {
    svgRef,
    updateTextBoxPositions,
    canRenderSvgText,
    filteredCenterlineItems,
  } = useLabelNamingText({
    context,
    pool,
    lowQuality,
  });

  useResizeObserver({
    context,
    vtkContainerRef: ref,
    updateTextBoxPositions: () => {
      updateTextBoxPositions();
      updateMeasurementLabelPositions();
    },
  });

  renderers[panelConfig.componentKey] = {
    shouldSuppressRenderError: () => isServerFrontDataVtkMode,
    render(args?: any) {
      const { type } = args ?? {};
      //if(type !== 'updateRenderWindow')
      // console.log(args);
      switch (type) {
        case 'focus':
          handleFocus(args);
          break;
        case 'rotate':
          handleRotate(args);
          break;
        case 'switchAxes':
          handleAxes(args);
          break;
        case 'updateRenderWindow':
          handleUpdateRenderWindow(args);
          break;
        case 'markerMove':
        case 'polygonMove':
          handleMarkerMove(args);
          if (isLesionStackViewLayout) {
            applyLesionStackMidNodeVisibility();
          }
          break;
        case 'stenosisUpdate':
          if (isServerFrontDataVtkMode && analysisType !== 'stenosis') return;
          centerLineRender(context, pool!, analysisType, []);
          applySurface3DOverlayVisibility();
          break;
        case 'reset': {
          handleReset(args, globalToolState?.toggles.ToggleAxes!);
          if (['chamber', 'ejectionFraction'].includes(analysisType)) {
            if (!chamberViewRef.current) break;
            updateCutter(
              context,
              chamberViewRef.current.index,
              chamberViewRef.current,
              true,
            );
            if (analysisType === 'ejectionFraction') {
              const world = context.current?.focus;
              if (!world) return;
              scheduleRender('Axial', { type: 'focus', world, request: true });
              axesLineRenderer(context);
            }
          } else if (isSegmentationShape) {
            if (isSegmentationShape && config.visibleMIP) {
              //MIP 처음 색상으로~
              updateVolumeMIPColor(windowWidth, windowLevel, invert);
            }
          }
          break;
        }
        case 'applySegVolumeMode': {
          if (!isSegmentationShape) return;
          const { mode, colorType } = args;
          const volumeKey =
            mode === 'runMIP'
              ? volumeNames.MIP
              : mode === 'runHeartCutter'
                ? volumeNames.heartCut
                : volumeNames.base;
          const targetVolumeId = config.heartCut
            ? volumeNames.heartCut
            : volumeNames.base;
          if (!ensureVolume(volumeKey)) return;

          const { enabled, target } = config.volumeDilation;
          const showVolume = async (visibleVolumeId: volumeKeys) => {
            if (enabled && !target?.includes(targetVolumeId)) {
              await runDilateDataVolume(
                context,
                selectedSeriesId,
                targetVolumeId,
                false,
              );
            }
            setSegVolumeVisibility(visibleVolumeId);
          };
          if (mode === 'runHeartCutter') {
            showVolume(targetVolumeId);
          } else if (mode === 'runMIP') {
            showVolume(config.visibleMIP ? volumeNames.MIP : targetVolumeId);
            updateVolumeMIPColor(
              windowWidth,
              windowLevel,
              config.visibleMIP ? invert : false,
            );
          } else if (mode === 'updateVolumeColor') {
            setSegVolumeVisibility(targetVolumeId);
            updateVolumeColor(context, selectedSeriesId!, colorType);
          }
          break;
        }
        case 'updateWindowLevel': {
          if (isSegmentationShape && config.visibleMIP) {
            updateVolumeMIPColor(
              args.windowWidth,
              args.windowLevel,
              args.invert,
            );
          }
          break;
        }
        case 'chamberViewProps': {
          const {
            chamberViewProps,
            moveAxes = true,
            mode,
            newIndex,
            run,
          } = args;
          if (!chamberViewProps || !context.current) break;
          const prev = chamberViewRef.current;
          chamberViewRef.current = {
            viewNormal: chamberViewProps.viewNormal,
            normal: chamberViewProps.normal,
            centers: chamberViewProps.centers,
            index: newIndex,
          };

          if (run) {
            if (!ensureVolume('volume')) return;
          }

          const rotate =
            mode !== 'guidePan' &&
            mode !== 'guideSliceMove' &&
            prev.viewNormal !== chamberViewProps.viewNormal &&
            prev.normal !== chamberViewProps.normal;

          const { renderer } = context.current;
          const scale = renderer.getActiveCamera().getParallelScale();

          if (rotate) {
            initCameraForAnalysis(context, analysisType, selectedSeriesId!);
            renderer.getActiveCamera().setParallelScale(scale);
          }
          updateCutter(context, newIndex, chamberViewProps, rotate);

          if (moveAxes) axesLineRenderer(context);

          break;
        }
        case 'updateLabelmap': {
          if (analysisType === 'ejectionFraction') {
            setIsReady(true);
          }
          break;
        }
        case 'updateSegmentation': {
          const { name, newSegmentation } = args;
          if (!newSegmentation) return;
          const seg = getRequestSegmentation(analysisType, newSegmentation!);
          const visibleKeys: string[] = Object.keys(seg)
            .filter((key) => seg[key as keyof typeof seg].visibility)
            .filter((key) => key !== 'calcium');
          if (
            name === 'calcium' &&
            context.current?.actorIds[volumeNames.calcium]
          ) {
            const visibility = seg['calcium'].visibility;
            context.current?.actorIds[volumeNames.calcium].setVisibility(
              visibility,
            );
            handleUpdateRenderWindow(args);
          } else {
            updateSegVolume({ visibleKeys, refreshHeartCut: true });
          }

          //node on/off 함수인데 vr은 coronary 또는 aorta 고정
          const key =
            analysisType === 'aorticValveCalcium' || analysisType === 'tavi'
              ? 'aorta'
              : 'coronary';
          handleVisibility(
            context,
            analysisType,
            key,
            newSegmentation[key],
            setSvgShowText,
          );
          applyLesionStackMidNodeVisibility();
          applySurface3DOverlayVisibility();
          break;
        }
        case 'UpdateEF':
          createEFVolume(selectedStudySeries[args.index].id);
          break;
        case 'lightingChange':
          const { ambient, enabled } = args;
          setData((prev) => ({
            lightingOptions: {
              ...prev.lightingOptions,
              VR: {
                enabled,
                ambient,
              },
            },
          }));
          updateAmbientLight(context, { ambient, enabled });
          handleUpdateRenderWindow(args);
        default:
          break;
      }
    },
  };

  useEffect(() => {
    if(analysisType === 'chamber') {
      //챔버는 이제 다이콤패널에서 먼저 데이터 로딩끝나고 그뒤에 만들꺼여
        if (selectedPanelComponentKey !== 'Axial') {
          updateSelectedPanelConfig(
            selectedLayoutConfig.panelConfig.filter(
              (p) => p.panelId === 'DICOMPanel',
            )[0],
          );
        }
      return
    };
   if (context.current) {
     context.current.serverFrontDataVtkMode = isServerFrontDataVtkMode;
     context.current.activeAnalysisType = isServerFrontDataVtkMode ? analysisType : undefined;
     context.current.activeSeriesId = isServerFrontDataVtkMode ? selectedSeriesId : undefined;
     context.current.serverFrontDataRenderBlocked = isServerFrontDataVtkMode;
   }
   createVolume();
   setRenderingModeMIP(config.renderingModeMIP);
  }, [isReady,analysisType]);

  useEffect(() => {
    if (!context.current || analysisType === 'MPR' || !baseVolumeReady) return;
    const { renderer, actorIds } = context.current;
    const currentVolumeName = getSegShapeAnalysisType(analysisType)
      ? volumeNames.base
      : config.visibleMIP
        ? volumeNames.MIP
        : config.heartCut
          ? volumeNames.heartCut
          : volumeNames.base;
     const targetVolume = actorIds[currentVolumeName];
    console.log('volumeDilation enabled changed', baseVolumeReady, config.volumeDilation.enabled);
     const exists = renderer.getVolumes().some((v) => v === targetVolume);
     if (exists) {
       //있을때만 업데이트
       updateSegVolume({});
     }
   }, [config.volumeDilation.enabled]);

  useEffect(() => {
    if(!context.current) return;
    if (renderingModeMIP === config.renderingModeMIP) return;
    if(!['plaque','segmentation'].includes(analysisType)) {
      return;
    };
    const {renderer, actorIds} = context.current;
    const targetVolume = actorIds[volumeNames.MIP];
    const exists = renderer.getVolumes().some(v => v === targetVolume);
    if(exists){
      //있을때만 업데이트
      console.log({pre: renderingModeMIP, current: config.renderingModeMIP});
      updateSegVolume({refreshMIP: true});
    }
    setRenderingModeMIP(config.renderingModeMIP);
  }, [config.renderingModeMIP]);

  const readyHighlight =
    analysisType !== 'myocardial' &&
    pool?.centerData.loaded &&
    pool?.segmentationVolumeMapper.loaded;
    //&& pool?.mapping_branches.loaded
  const editLumenInitialPointsSignature = useMemo(() => {
    const coronary = (
      pool?.parameter?.data as { SavedInfo?: SavedInfo } | undefined
    )?.SavedInfo?.Coronary;

    return [
      coronary?.initialPoints1?.join(',') ?? '',
      coronary?.initialPoints2?.join(',') ?? '',
    ].join('|');
  }, [pool?.parameter?.data]);

  useEffect(() => {
    /**
     * fai mainBranch 3개
     * plaque 1개
     * node랑 lumen 한꺼번에 랜더링 처리
     * fai에서는 한번만 들어오겠지
     * plaque에서는 branchId 바꾸면 들어오겠지
     */
    if (!readyHighlight) return;
    if (!pool) return;
    const generation = vrServerGenerationRef.current;
    if (isStaleServerVrGeneration(generation)) return;
    const branch: number[] = [];
    switch (analysisType) {
      case 'tavi':
        branch.push(config.tavi_aorta_id);
        break;
      case 'fai':
      case 'calciumScore':
        branch.push(...mainBranchIds.slice(0, 3));
        break;
      case 'plaque':
      case 'stenosis':
      case 'isr':
        branch.push(plaueBranchMainId);
        break;
    }
    if (!branch.length) {
      if (context.current) context.current.serverFrontDataRenderBlocked = false;
      return;
    }
    if (isStaleServerVrGeneration(generation)) return;
    centerLineRender(context, pool!, analysisType, branch);
    if (!['calciumScore','tavi'].includes(analysisType)) {
      void CPRNodeAllRenderer(context, pool, branch, stateMap).then(() => {
        applyLesionStackMidNodeVisibility();
        applySurface3DOverlayVisibility();
      });
    }

    const key =
      analysisType === 'aorticValveCalcium' || analysisType === 'tavi'
        ? 'aorta'
        : 'coronary';

    const seg = panelStates.Surface3D?.segmentation;
    if (seg) {
      handleVisibility(context, analysisType, key, seg[key], setSvgShowText);
      applyLesionStackMidNodeVisibility();
      applySurface3DOverlayVisibility();
    } else {
      applySurface3DOverlayVisibility();
    }
    if (context.current) {
      context.current.serverFrontDataRenderBlocked = false;
      scheduleRender(panelConfig.componentKey, { type: 'updateRenderWindow' });
    }
  }, [
    readyHighlight,
    analysisType,
    plaueBranchMainId,
    isServerFrontDataVtkMode,
    selectedSeriesId,
    applyLesionStackMidNodeVisibility,
    applySurface3DOverlayVisibility,
  ]);

  useEffect(() => {
    const current = context.current;
    if (!current) return;

    const clearMarkers = () => {
      const changed = clearInitialPointSphereMarkers(current);
      if (changed) {
        scheduleRender(current.viewportId, { type: 'updateRenderWindow' });
      }
    };

    if (
      analysisType !== 'FFR' ||
      config.EditMode !== EditNames.lumen ||
      !selectedSeriesId ||
      !pool
    ) {
      clearMarkers();
      return;
    }

    const volume = getVolumeFromSeriesId(selectedSeriesId, lowQuality);
    if (!volume) {
      clearMarkers();
      return;
    }

    const spacing = volume.spacing as [number, number, number];
    const origin = volume.origin as [number, number, number];
    const dimensions = volume.dimensions as [number, number, number];
    const markerRadius = Math.max(2, Math.min(6, Math.max(...spacing) * 3));
    const specs = buildInitialPointSphereMarkerSpecs({
      parameter: pool.parameter?.data as { SavedInfo?: SavedInfo } | undefined,
      spacing,
      origin,
      dimensions,
      half: lowQuality,
      indexToWorld: getIndexToWorld,
    });
    const changed = syncInitialPointSphereMarkers(current, specs, {
      radius: markerRadius,
    });

    if (changed) {
      scheduleRender(current.viewportId, { type: 'updateRenderWindow' });
    }

    return clearMarkers;
  }, [
    analysisType,
    config.EditMode,
    editLumenInitialPointsSignature,
    lowQuality,
    pool,
    selectedSeriesId,
  ]);

  useEffect(() => {
    /**
     * fai에서만 리셋버튼누르면 동작하는부분
     */
    if(analysisType !== 'fai' || !resetNodeRef.current) return;
    if(!stateMap || !pool) return;
    const generation = vrServerGenerationRef.current;
    if (isStaleServerVrGeneration(generation)) return;
    void CPRNodeAllRenderer(context,pool,mainBranchIds.slice(0, 3),stateMap).then(() => {
      applySurface3DOverlayVisibility();
    });
    resetNodeRef.current = false;
  },[resetNodeRef, stateMap, applySurface3DOverlayVisibility])

  const readyTaviData =
    analysisType === 'tavi' &&
    !!pool?.['tavi.annulus']?.loaded &&
    !!pool?.['tavi.sinotubularjunction']?.loaded &&
    !!pool?.['tavi.sinus']?.loaded &&
    !!pool?.['tavi.parsed']?.data &&
    !!pool?.centerData.loaded &&
    !!pool?.segmentationVolumeMapper.loaded &&
    baseVolumeReady;
    //&& pool?.mapping_branches.loaded


  useEffect(() => {
    if(!selectedSeriesId) return;
    /**
     * tavi 전용~
     */
    if (!readyTaviData) return;
    console.log('tavi node 왔능가', pool?.['tavi.parsed']);

    taviNodeAllRenderer(context, pool, selectedSeriesId);
    applySurface3DOverlayVisibility();
    //CPRNodeAllRenderer(context, pool, mainBranchIds.slice(0, 3), stateMap);
  }, [readyTaviData, applySurface3DOverlayVisibility]);


  useEffect(() => {
    if (!ref.current) return;
    const container = ref.current;
    const mouseMoveHandler = (evt: MouseEvent) =>
      handleMouseMove({
        context,
        evt,
        toolName:panelToolState?.activeToolId,
        voi:currentVOI,
        analysisType,
        updateWindowLevelWidth,
      });
    const mouseUpHandler = (evt: MouseEvent) =>
      handleMouseUp(context, evt, analysisType, selectedSeriesId);
    const mouseDownHandler = (evt: MouseEvent) => handleMouseDown(context, evt, analysisType);
    const mouseWheelHandler = (evt: WheelEvent) =>  handleMouseWheel(context, evt);

    container.addEventListener('mousedown', mouseDownHandler, { passive: false });
    container.addEventListener('mouseup', mouseUpHandler, { passive: false });
    container.addEventListener('mousemove', mouseMoveHandler, { passive: false });
    container.addEventListener('wheel', mouseWheelHandler, { passive: false });

    return () => {
      container.removeEventListener('mousemove', mouseMoveHandler);
      container.removeEventListener('mousedown', mouseDownHandler);
      container.removeEventListener('mouseup', mouseUpHandler);
      container.removeEventListener('wheel', mouseWheelHandler);
    };
  }, [
    context,
    panelToolState,
    updateWindowLevelWidth,
    currentVOI,
    analysisType,
    selectedSeriesId,
  ]);


  //패널 상태 업뎃
  // const handleUpdatePanelState = (nextPanelState: Partial<PanelState>) => {
  //     updatePanelState(panelConfig.componentKey,nextPanelState);
  // };

  //열/닫
  const handlePopupOpen = (key: keyof VRPanelPopup) => {
    setOpen({ ...initialPopup, [key]: true });
  };
  const handlePopupClose = (key: keyof VRPanelPopup) => {
    setOpen({ ...initialPopup, [key]: false });
  };
  // const panelState = useMemo(() => getPanelState(panelConfig.componentKey),[handleUpdatePanelState, panelConfig.componentKey])

  //프로젝숀 아이템

  const projectionContextMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const only3DSwitchType =isVRSurfaceSwitchType(analysisType);
    const finalProjection =
      only3DSwitchType
        ? ['VR','Surface3D']
        : possibleProjection ?? [];

    if (finalProjection.length === 0) {
      handlePopupClose('projection');
    }
    return finalProjection!
      .filter((key) => key !== panelConfig.componentKey)
      .map((key) => ({
        label: key,
        onClick: () => {
          swapLayoutPanelConfig(
            panelConfig.componentKey,
            key as PanelComponentKeyType
          );
          if(analysisType === 'segmentation' && !only3DSwitchType)
            updateOtherViews([panelConfig.componentKey, key]);
          handlePopupClose("projection");
        },
        isChecked: panelConfig.componentKey === key,
      }));
  }, [open["projection"],]);

  //패널 상태 업뎃
  const handleUpdatePanelState = useCallback((key: keyof PanelState | string, value: PanelState[keyof PanelState])=>{
    //if(refComponentKey === componentKey) return;
    updatePanelState(refComponentKey,key,value);
  },[updatePanelState, refComponentKey])

  //프리셋 에디터
  const extraPresetContextMenuItem: SimpleContextMenuItem[] = useMemo(
    () => [
      {
        label: CT_PRESET_EDITOR_NAME,
        onClick: () => {
          handlePopupClose('preset');
          openCTPreset({
            componentKey:panelConfig.componentKey,
          });
        },
      },
    ],
    [open['preset'],currentPreset,windowLevel,windowWidth,handleUpdatePanelState],
  );

  //프리셋 아이템
  const presetContextMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const presetMapper = (preset?: typeof currentPreset) => {
      if (!preset) return [];
      // const allPanel =  globalToolState?.toggles.ToggleSync;
      return Object.entries(preset).map(([key, item]) => ({
        label: item.label,
        onClick: () => {
          if (!selectedSeriesId) return;
          let ww = item.windowWidth;
          let wl = item.windowLevel;
          let rawMin: number = 0;
          let rawMax: number = 0;
          if (key === "default") {
            ww = Number(selectedSeriesImageTag?.windowWidth);
            wl = Number(selectedSeriesImageTag?.windowCenter);
          } else if (key === "fullDynamic") {
            [rawMin, rawMax] = getVolumeMinMax(
              selectedSeriesId,
              renderingQuality !== 'high',
            );
            ww = rawMax - rawMin;
            wl = (rawMax + rawMin) / 2;
          }else if(key === "negative"){
            ww = windowWidth;
            wl = windowLevel;
          }


          let negative = invert;
          if (key !== "negative") {
            // updateViewportsWindowLevelWidth(wl,ww);
            updateWindowLevelWidth([wl,ww])
          } else {
            negative = !negative;
            updateVOIInvert(negative);
          }

          updateVolumeMIPColor(ww, wl, negative);

          handlePopupClose("preset");
        },

        // isChecked: key === "negative" && switchConfigs.switchCTNegativeIntensity,
        isChecked: key === "negative" && invert,
      }));
    };
    return [
      // ...presetMapper(defaultPreset),
      ...presetMapper(currentPreset),
    ];
  }, [open["preset"], currentPreset, selectedSeries, selectedSeriesImageTag,invert]);


  const bottomRightContent = useMemo(() => {
    if (!config.visibleMIP || !isSegmentationShape) return null;
    return (
      <>
        <div className="popup-text" onClick={() => handlePopupOpen('preset')}>
          {`WL: ${windowLevel} WW: ${windowWidth}`}
        </div>
      </>
    );
  }, [windowLevel, windowWidth, config.visibleMIP]);

  const bottomLeftContent = useMemo(
    () => (
       <>
      { !!CArmAngles &&
        <div className="projection-text" onClick={() => handlePopupOpen('cArmAngles')}>
          {`${CArmAngles.primary} ${CArmAngles.secondary}`}
        </div>
      }
      <div
        className={`projection-text ${panelConfig.componentKey}`}
        onClick={(e) => {
          if (analysisType === 'chamber' || analysisType === 'ejectionFraction')
            return;
          handlePopupOpen('projection');
        }}
      >
        {panelConfig.componentKey}
      </div>
      </>
    ),
    [windowLevel, windowWidth,CArmAngles],
  );
  //console.log(panelState)
  // 패널 내 오버레이에 필요한 상태나 계산 로직이 있을 수 있음
  const memoizedClassName = useMemo(
    () =>
      `panel ${className}${fullscreenPanelComponentKey === panelConfig.componentKey ? ' fullscreen' : ''}`,
    [
      fullscreenPanelComponentKey,
      className,
      // panelState.synchronizingTarget
    ],
  );

  // 더블 클릭시 전체 화면
  const hanldleFullScreenPanel = () => {
    if (isPreview) return;
    if (panelConfig.componentKey === fullscreenPanelComponentKey)
      updateFullscreenPanelComponentKey(undefined);
    else updateFullscreenPanelComponentKey(panelConfig.componentKey);
  };

  return (
    <div
      className={memoizedClassName}
      onMouseDown={() => {
        //챔바일때는 절~~대 안되야해
        if (analysisType !== 'chamber') {
          if (isToolInteracting()) return;
          updateSelectedPanelConfig({
            ...panelConfig,
            componentKey: refComponentKey,
          });
        }
      }}
      onDoubleClick={() => {
        if (Object.values(open).some(Boolean) || analysisType === 'chamber')
          return;
        hanldleFullScreenPanel();
      }}
    >
      <div className={`renderer ${panelConfig.componentKey}`} ref={ref} />
      {/* {showSpline && analysisType === 'MPR' && (
        <SvgDrawLine
          containerRef={ref}
          renderer={context.current?.renderer}
          onCommit={handleComplete}
        />
      )} */}
      {globalToolState?.toggles.ToggleAnnotations && (
        <>
          {' '}
          <PanelOverlay
            config={{
              left: {
                content: analysisType === 'segmentation' ? topContent : '',
              },
              'top-left': {
                content:
                  analysisType === 'MPR'
                    ? topLeftContent
                    : analysisType === 'segmentation'
                      ? topLeftContentForCAC
                      : '',
              },
              'top-right': {
                content: topRightContent,
              },
              'bottom-left': {
                content: (
                  <>
                    {midLeftContentRRPercent}
                    {bottomLeftContent}
                    {surface3DOverlayButtonContent}
                  </>
                ),
              },
              'bottom-right': {
                content: bottomRightContent,
              },
            }}
            originComponentKey={panelConfig.componentKey}
          />
        </>
      )}

      {canRenderSvgText &&
        svgShowText &&
        surface3DOverlayLabel.label !== false &&
        !config.isCapture && (
        <SvgTextRenderer
          svgRef={svgRef}
          containerRef={ref}
          renderer={context.current!.renderer}
          dataSource={filteredCenterlineItems}
          onCameraRotate={updateTextBoxPositions}
        />
      )}
      {measurementLabelItems.length > 0 && (
        <SvgMeasurementText
          svgRef={measurementSvgRef}
          containerRef={ref}
          labels={measurementLabelItems}
        />
      )}
      {open['projection'] && (
        <SimpleContextMenu
          items={projectionContextMenuItems}
          onClose={() => handlePopupClose('projection')}
        />
      )}
      {surface3DOverlayMenuOpen && shouldShowSurface3DOverlayMenu && (
        <SimpleContextMenu
          items={surface3DOverlayMenuItems}
          onClose={() => setSurface3DOverlayMenuOpen(false)}
          checkbox={true}
        />
      )}
      {open['cArmAngles'] &&
        handleOpenCArmAnglesModal(() => handlePopupClose('cArmAngles'))}
      {config.visibleMIP && open['preset'] && (
        <SimpleContextMenu
          items={presetContextMenuItems}
          ExtraActions={extraPresetContextMenuItem}
          onClose={() => handlePopupClose('preset')}
        />
      )}
    </div>
  );
};

export default VRPanel;
